<template>
  <div>
    <div v-if="localVisible" class="br-name-change-modal">
      <div class="br-name-change-modal__scrim" @click="close"></div>
      <div
        class="br-name-change-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="br-name-change-title"
      >
        <div class="br-name-change-modal__header">
          <h2 id="br-name-change-title" class="br-name-change-modal__title">
            {{ $t('lbl_EMPBE_BR_RequestNameChange') }}
          </h2>
          <p class="br-name-change-modal__desc">{{ $t('lbl_EMPBE_BR_NameChange_Modal_Desc') }}</p>
        </div>

        <div class="br-name-change-modal__section">
          <p class="br-name-change-modal__label">
            {{ $t('lbl_EMPBE_BR_NameChange_UpdateDisplayName') }}
          </p>

          <label class="br-name-change-modal__outlined">
            <span class="br-name-change-modal__outlined-label-row">
              <span class="br-name-change-modal__outlined-label">
                {{ $t('lbl_EMPBE_BR_NameChange_DisplayName_Zh') }}
              </span>
            </span>
            <input
              v-model="displayNameZh"
              type="text"
              class="br-name-change-modal__outlined-input"
              :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_Zh_Placeholder')"
            />
          </label>

          <label class="br-name-change-modal__outlined">
            <span class="br-name-change-modal__outlined-label-row">
              <span class="br-name-change-modal__outlined-label">
                {{ $t('lbl_EMPBE_BR_NameChange_DisplayName_En') }}
              </span>
            </span>
            <input
              v-model="displayNameEn"
              type="text"
              class="br-name-change-modal__outlined-input"
              :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_En_Placeholder')"
            />
          </label>
        </div>

        <p v-if="errorMessage" class="br-name-change-modal__error">{{ errorMessage }}</p>

        <div class="br-name-change-modal__actions">
          <button type="button" class="br-name-change-modal__cancel" @click="close">
            {{ $t('lbl_EMPBE_BR_NameChange_Cancel') }}
          </button>
          <button
            type="button"
            class="br-name-change-modal__submit"
            :disabled="submitting || showDuplicatePendingConfirm"
            @click="submit"
          >
            {{ $t('lbl_EMPBE_BR_NameChange_Request') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 与上传新执照同一套 DuplicatePending 确认框 -->
    <BrConfirmModal
      :visible="showDuplicatePendingConfirm"
      :title="$t('lbl_EMPBE_BR_DuplicatePending_Title')"
      :body="$t('lbl_EMPBE_BR_DuplicatePending_Body')"
      :cancel-label="$t('lbl_EMPBE_BR_DuplicatePending_Cancel')"
      :confirm-label="$t('lbl_EMPBE_BR_DuplicatePending_Submit')"
      :submitting="submitting"
      :close-on-scrim="false"
      @cancel="closeDuplicatePendingConfirm"
      @confirm="confirmDuplicatePendingSubmit"
    />
  </div>
</template>

<script>
import { BusinessRegistrationService } from '../../../_services/BusinessRegistration.Service';
import {
  getBrSubmissionApprovalId,
  resolveBrItemRegistrationNo,
} from '../utils/brDocument';
import { getApprovedBrForNameChangeOptions } from '../utils/brNameChange';
import { refreshBrListsForDuplicateCheck } from '../utils/brDuplicatePendingRefresh';
import brDuplicatePendingGuard from '../mixins/brDuplicatePendingGuard';
import BrConfirmModal from './BrConfirmModal.vue';

export default {
  name: 'BrNameChangeModal',
  components: {
    BrConfirmModal,
  },
  mixins: [brDuplicatePendingGuard],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    documentList: {
      type: Array,
      default: () => [],
    },
    /** 来自当前生效卡铅笔 / Set as Current：作为更名 sourceApprovalId */
    sourceApprovalId: {
      type: [Number, String],
      default: null,
    },
    currentDisplayNameEn: {
      type: String,
      default: '',
    },
    currentDisplayNameLocal: {
      type: String,
      default: '',
    },
    empCompanyId: {
      type: [Number, String],
      default: null,
    },
    accountId: {
      type: [Number, String],
      default: null,
    },
    submitType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localVisible: false,
      resolvedSourceApprovalId: null,
      displayNameEn: '',
      displayNameZh: '',
      submitting: false,
      errorMessage: '',
    };
  },
  computed: {
    approvedBrItems() {
      return getApprovedBrForNameChangeOptions(this.documentList);
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.localVisible = value;
        if (value) {
          this.resetForm();
        }
      },
    },
    localVisible(value) {
      if (!value && this.visible) {
        this.$emit('close');
      }
    },
  },
  methods: {
    resetForm() {
      this.errorMessage = '';
      // 更名申请不预填现有展台名，由供应商自行输入
      this.displayNameEn = '';
      this.displayNameZh = '';
      this.resolvedSourceApprovalId = this.resolveSourceApprovalId();
      this.showDuplicatePendingConfirm = false;
      this.pendingDuplicateSubmitPayload = null;
    },
    resolveSourceApprovalId() {
      const fromProp = Number(this.sourceApprovalId);
      if (Number.isFinite(fromProp) && fromProp > 0) {
        const matched = this.approvedBrItems.some(
          (item) => Number(getBrSubmissionApprovalId(item)) === fromProp,
        );
        if (matched) {
          return fromProp;
        }
      }
      const first = this.approvedBrItems[0];
      const id = Number(getBrSubmissionApprovalId(first));
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    resolveSourceRegistrationNo() {
      const sourceId = Number(this.resolvedSourceApprovalId);
      if (!Number.isFinite(sourceId) || sourceId <= 0) {
        return '';
      }
      const source = this.approvedBrItems.find(
        (item) => Number(getBrSubmissionApprovalId(item)) === sourceId,
      );
      const raw = resolveBrItemRegistrationNo(source);
      // resolveBrItemRegistrationNo 无号时回退为「—」，不能参与同号匹配
      if (!raw || raw === '—') {
        return '';
      }
      return raw;
    },
    close() {
      this.showDuplicatePendingConfirm = false;
      this.pendingDuplicateSubmitPayload = null;
      this.localVisible = false;
    },
    isDuplicatePendingBusy() {
      return !!this.submitting;
    },
    validate() {
      if (!this.resolvedSourceApprovalId) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_SelectBr');
        return false;
      }
      const en = (this.displayNameEn || '').trim();
      const zh = (this.displayNameZh || '').trim();
      if (!en && !zh) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_Required');
        return false;
      }
      const currentEn = (this.currentDisplayNameEn || '').trim();
      const currentZh = (this.currentDisplayNameLocal || '').trim();
      if (en === currentEn && zh === currentZh) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_NoChange');
        return false;
      }
      this.errorMessage = '';
      return true;
    },
    buildPayload() {
      const payload = {
        sourceApprovalId: Number(this.resolvedSourceApprovalId),
        displayNameEn: (this.displayNameEn || '').trim(),
        displayNameZh: (this.displayNameZh || '').trim(),
      };
      const empCompanyId = Number(this.empCompanyId);
      if (Number.isFinite(empCompanyId) && empCompanyId > 0) {
        payload.empCompanyId = empCompanyId;
      }
      const accountId = Number(this.accountId);
      if (Number.isFinite(accountId) && accountId > 0) {
        payload.accountId = accountId;
      }
      const submitType = String(this.submitType || '').trim();
      if (submitType) {
        payload.submitType = submitType;
      }
      return payload;
    },
    async refreshListsForDuplicateCheck() {
      const empCompanyId = Number(this.empCompanyId);
      return refreshBrListsForDuplicateCheck({
        fromAdmin: Number.isFinite(empCompanyId) && empCompanyId > 0,
        empCompanyId,
      });
    },
    async submit() {
      if (!this.validate()) {
        return;
      }
      if (this.submitting || this.showDuplicatePendingConfirm) {
        return;
      }

      const payload = this.buildPayload();
      const registrationNo = this.resolveSourceRegistrationNo();
      this.submitting = true;
      try {
        const refreshed = await this.refreshListsForDuplicateCheck();
        if (!refreshed.ok) {
          this.errorMessage = this.$t('lbl_EMPBE_BR_List_LoadFailed');
          return;
        }
        if (this.hasPendingBrDuplicates(
          refreshed.list,
          registrationNo,
          refreshed.nameChanges,
        )) {
          this.openDuplicatePendingConfirm(payload);
          return;
        }
        await this.doRequestDisplayNameChange(payload);
      } catch (e) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Failed');
      } finally {
        this.submitting = false;
      }
    },
    async executeDuplicatePendingSubmit(payload) {
      this.submitting = true;
      try {
        await this.doRequestDisplayNameChange(payload);
      } catch (e) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Failed');
      } finally {
        this.submitting = false;
      }
    },
    async doRequestDisplayNameChange(payload) {
      const res = await BusinessRegistrationService.RequestDisplayNameChange(payload);
      if (!res?.isSuccessful) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Failed');
        return;
      }
      this.$emit('submitted', res.data);
      this.localVisible = false;
    },
  },
};
</script>
